import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getAutoLabelColor } from '@/lib/colors';
import { cn } from '@/lib/utils';

interface AutoLabelBadgeProps {
  label: {
    id?: string;
    name: string;
    color?: string | null;
  };
  className?: string;
  title?: string;
  reason?: string | null;
  onRemove?: () => void;
  showRemove?: boolean;
}

export function AutoLabelBadge({ label, className, title, reason, onRemove, showRemove = false }: AutoLabelBadgeProps) {
  const color = getAutoLabelColor(label.color);

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'border-transparent px-2.5 py-0.5 text-[11px] font-medium shadow-sm transition-all hover:brightness-95 rounded-full group/badge',
        showRemove && onRemove && 'pr-1.5',
        className,
      )}
      style={{
        backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
        color: `color-mix(in srgb, ${color} 90%, black)`,
        borderColor: `color-mix(in srgb, ${color} 25%, transparent)`,
      }}
      title={!reason ? (title ?? label.name) : undefined}
    >
      {label.name}
      {showRemove && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove();
          }}
          className="ml-1 rounded-full p-0.5 hover:bg-black/10 transition-colors opacity-60 hover:opacity-100"
          aria-label={`Remove ${label.name} label`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );

  if (reason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <p>{reason}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return badge;
}
